
import React from 'react';
import { AllocationClassification } from "../../proto/security_classification";
import {Doughnut} from "../../../client/components/doughnut";

// Labels
const CASH = "Cash";
const STOCK = "Stock";
const BOND = "Bond";
const REAL_ESTATE = "Real Estate";
const GOLD = "Gold";
const COMMODITIES = "Commodities";
const CRYPTO = "Crypto";

const ALL_LABELS = [
  CASH, STOCK, BOND, REAL_ESTATE, GOLD, COMMODITIES, CRYPTO
];

/**
 * Maps an AllocationClassification to a display category.
 */
const CLASSIFICATION_LUT = {
  [AllocationClassification.CASH.id]: CASH,
  [AllocationClassification.LARGE_VALUE.id]: STOCK,
  [AllocationClassification.LARGE_BLEND.id]: STOCK,
  [AllocationClassification.LARGE_GROWTH.id]: STOCK,
  [AllocationClassification.MID_CAP_VALUE.id]: STOCK,
  [AllocationClassification.MID_CAP_BLEND.id]: STOCK,
  [AllocationClassification.MID_CAP_GROWTH.id]: STOCK,
  [AllocationClassification.SMALL_VALUE.id]: STOCK,
  [AllocationClassification.SMALL_BLEND.id]: STOCK,
  [AllocationClassification.SMALL_GROWTH.id]: STOCK,
  [AllocationClassification.FOREIGN_LARGE_BLEND.id]: STOCK,
  [AllocationClassification.FOREIGN_LARGE_VALUE.id]: STOCK,
  [AllocationClassification.FOREIGN_LARGE_GROWTH.id]: STOCK,
  [AllocationClassification.FOREIGN_SMALL_MID_BLEND.id]: STOCK,
  [AllocationClassification.FOREIGN_SMALL_MID_VALUE.id]: STOCK,
  [AllocationClassification.FOREIGN_SMALL_MID_GROWTH.id]: STOCK,
  [AllocationClassification.DIVERSIFIED_EMERGING_MARKETS.id]: STOCK,
  [AllocationClassification.EUROPE_STOCK.id]: STOCK,
  [AllocationClassification.JAPAN_STOCK.id]: STOCK,
  [AllocationClassification.CHINA_REGION.id]: STOCK,
  [AllocationClassification.UTILITIES.id]: STOCK,
  [AllocationClassification.SHORT_GOVERNMENT.id]: BOND,
  [AllocationClassification.INTERMEDIATE_GOVERNMENT.id]: BOND,
  [AllocationClassification.LONG_GOVERNMENT.id]: BOND,
  [AllocationClassification.ULTRASHORT_BOND.id]: BOND,
  [AllocationClassification.SHORT_TERM_BOND.id]: BOND,
  [AllocationClassification.CORPORATE_BOND.id]: BOND,
  [AllocationClassification.LONG_TERM_BOND.id]: BOND,
  [AllocationClassification.HIGH_YIELD_BOND.id]: BOND,
  [AllocationClassification.WORLD_BOND.id]: BOND,
  [AllocationClassification.HIGH_YIELD_MUNI.id]: BOND,
  [AllocationClassification.INFLATION_PROTECTED_BOND.id]: BOND,
  [AllocationClassification.REAL_ESTATE.id]: REAL_ESTATE,
  [AllocationClassification.GLOBAL_REAL_ESTATE.id]: REAL_ESTATE,
  [AllocationClassification.GOLD.id]: GOLD,
  [AllocationClassification.COMMODITIES_BROAD_BASKET.id]: COMMODITIES,
  [AllocationClassification.CRYPTOCURRENCY.id]: CRYPTO,
}

const mult_color = (color, index) => {
  const mult = 0.92;
  const result = {};
  Object.keys(color).forEach(c => {
    result[c] = Math.round(color[c] * Math.pow(mult, index));
  });
  return result;
};

/**
 * @param alloc {Object}
 * @param client
 */
const gen_items_2 = (alloc, client) => {
  const items = [];
  const chart_colors = client.chart_colors;
  const palette = [
    chart_colors.stocks,
    chart_colors.bonds,
    chart_colors.alt,
  ];
  const labels = {};
  ALL_LABELS.forEach(label => {
    labels[label] = {
      pct: 0., label, color: {},
    }
  })

  // We're basically mapping investment categories into ALL_LABELS
  Object.keys(alloc).forEach(id => {
    if (id[0] !== "_") {
      if (!(id in AllocationClassification) || !(id in CLASSIFICATION_LUT)) {
        console.log("Bad classification", id);
      } else {
        const label = CLASSIFICATION_LUT[id];
        labels[label].pct += alloc[id];
      }
    }
  });

  Object.keys(labels).forEach((label, index) => {
    if (labels[label].pct === 0.) {
      delete labels[label];
    } else {
      const base_color = palette[index % palette.length];
      const mult = Math.floor(index / palette.length);
      Object.assign(labels[label].color, mult_color(base_color, mult));
      items.push(labels[label]);
    }
  });

  return items;
}

const dump_col = (categories) => {
  const li_style = {
    fontSize: "0.8rem",
  }
  return (
    <div style={{ flex: "1"}}>
      <ul>
        { categories.map(id => {
          return (
            <li style={li_style} key={id}>
              {AllocationClassification[id].label}
            </li>);
        })}
      </ul>
    </div>
  )
}
const dump_all_categories = (alloc) => {

  const categories = Object.keys(alloc).filter(id => id[0] !== "_");
  const full_rows = Math.floor(categories.length / 3.0);
  const rem = categories.length % 3;
  let col_0, col_1, col_2;
  if (rem === 0) {
    col_0 = col_1 = col_2 = full_rows;
  } else if (rem === 1) {
    col_0 = full_rows + 1;
    col_1 = full_rows;
    col_2 = full_rows;
  } else if (rem === 2) {
    col_0 = full_rows + 1;
    col_1 = full_rows + 1;
    col_2 = full_rows;
  }

  return (
    <div style={{display: "flex"}}>
      { dump_col(categories.slice(0, col_0)) }
      { dump_col(categories.slice(col_0, col_0 + col_1)) }
      { dump_col(categories.slice(col_0 + col_1, col_0 + col_1 + col_2)) }
    </div>
  )


}

const dump_metadata = alloc => {

  const title_style = {
    fontSize: "1.2rem",
    fontWeight: "bold",
  }

  const maybe_dump = (prop) => {
    const span_style = {
      fontWeight: "bold",
      paddingRight: "2rem",
    }
    if (prop in alloc) {
      return (
        <p>
          <span style={span_style}>
            {prop.substr(1).toUpperCase()}:
          </span>
          {alloc[prop]}
        </p>
      )
    }
    return null;
  }

  return (
    <div>
      <div style={{margin: "1rem 0"}}>
        <span style={title_style}>Alloc Debug</span>
      </div>
      { maybe_dump("_table_name") }
      { maybe_dump("_portfolio_years") }
      { maybe_dump("_risk") }
      { maybe_dump("_table_index") }
    </div>
  )
}

const AllocationDoughnut = props => {
  const size_rem = 30;
  const radius_cb = (theta) => {
    const cosx = Math.abs(Math.cos(theta));
    return 0.8 * (1.0 + cosx * 0.2);
  }

  return (
    <Doughnut
      sizeRem={size_rem}
      innerRad={0.4}
      outerRad={0.6}
      spokeRad={0.7}
      labelRad={radius_cb}
      containerStyle={props.containerStyle}
      items={gen_items_2(props.alloc, props.client)}
    />
  )
}
const InitialAllocation = props => {
  const title_style = {
    fontSize: "1.2rem",
    fontWeight: "bold",
  }
  const show_debug = props.component.state.show_debug;

  return (
    <div id={"allocation"}>
      <div style={{display: "flex", justifyContent: "center"}}>
        <div style={{flex: "1"}}>
          <AllocationDoughnut
            alloc={props.alloc}
            client={props.client}/>
        </div>
        <div style={{width: "5rem"}}/>
        <div style={{flex: "1"}}>
          <div style={{marginTop: "2rem"}}>
            <div style={{marginBottom: "1rem"}}>
              <span style={title_style}>Investment Categories</span>
            </div>
            { dump_all_categories(props.alloc) }
            { show_debug && dump_metadata(props.alloc) }
          </div>
        </div>
      </div>
    </div>
  )
};

const bad_allocation = alloc => {
  if (!alloc) {
    return true;
  }
  let result = false;
  Object.keys(alloc).forEach(cat => {
    if (cat[0] !== "_" && !(cat in AllocationClassification)) {
      result = true;
    }
  });
  return result;
}
export { InitialAllocation, bad_allocation, AllocationDoughnut, CLASSIFICATION_LUT };
